import { z } from 'zod';

/**
 * 运营统计的对外契约（C-168）。
 *
 * ## 一 这里放的是**口径**，不是数
 *
 * 每张统计表有哪几列、每一列的精确名词是什么、分子分母各是什么、源自哪张过程表、
 * 取数条件是什么——这些是**定义**，随代码走、随版本留痕。数在库里。
 *
 * 放在 contracts 而不是 api：详情页第 4 段（计算口径）与第 5 段（数据来源）
 * 是**界面要显示的东西**，前端得拿得到。放后端再开一个接口去取，
 * 就成了「同一份定义两处维护」，而两处不一致时界面会显示一个没人写过的口径。
 *
 * ## 二 名词纪律（C-168 A7）
 *
 * **每个数字带精确名词，不得出现「数量」「值」这类模糊词而无限定。**
 * 故 `label` 一律写全：「TaskRun 次数」不写「次数」，
 * 「基线单次耗时（分钟）」不写「耗时」。
 * 单位进 `label` 的括号，不另立一列——两处分开摆的结果是表头写「工时」、
 * 单位在图例里，而打印出来的那张纸上没有图例。
 *
 * ## 三 统计值必须标明它是统计结果（C-168 A7）
 *
 * `kind` 三档：
 *
 * | kind | 是什么 | 界面 |
 * |---|---|---|
 * | `dimension` | 这一行代表谁（部门、岗位、卡） | 不标 |
 * | `metric` | 统计出来的量 | 标「统计值」 |
 * | `derived` | 由同一行的其他统计列**在库里**派生（generated stored） | 标「统计值 · 派生」 |
 *
 * 本轮用文字标注即可；视觉标识组件由 C-167 出，后续统一挂。
 *
 * ## 四 `aggregate`：卡片上那个数是怎么合出来的
 *
 * 部门级与岗位级的表**一期多行**（一个部门一行 / 一张卡一行），
 * 卡片上只能显示一个数，所以必须说清它是合计还是均值——
 * 「12 个部门的卡位总数」与「每个部门平均卡位数」差一个数量级，
 * 而两个数长得一样。故 `aggregate` 是必填，且界面把它写出来。
 *
 * `none` 的列**不进卡片**：比率不能相加，也不能不加权地取平均。
 */

export const statLevelSchema = z.enum(['ent', 'dep', 'pos']);
export type StatLevel = z.infer<typeof statLevelSchema>;

export const STAT_LEVEL_LABEL: Record<StatLevel, string> = {
  ent: '企业级',
  dep: '部门级',
  pos: '岗位级',
};

export type StatColumnKind = 'dimension' | 'metric' | 'derived';

export const STAT_COLUMN_KIND_LABEL: Record<StatColumnKind, string> = {
  dimension: '维度',
  metric: '统计值',
  derived: '统计值 · 派生',
};

export type StatAggregate = 'sum' | 'avg' | 'none';

export const STAT_AGGREGATE_LABEL: Record<StatAggregate, string> = {
  sum: '合计',
  avg: '均值',
  none: '不可合并',
};

/**
 * 一列的显示格式。**放在口径里，不放在显示层**——
 * 「0.9」显示成 90% 还是 0.9 是这一列的属性，不是某个界面的偏好；
 * 两个界面各判一次，迟早有一处把压缩率画成 0.65 个百分点。
 */
export type StatFormat = 'text' | 'integer' | 'decimal1' | 'percent';

export type StatColumn = {
  /** 物理列名。**与库里的列一一对应**，不另起别名——别名会让「这个数在哪一列」多一层翻译。 */
  key: string;
  /** 精确名词，含单位。C-168 A7：不得出现「数量」「值」这类模糊词而无限定。 */
  label: string;
  kind: StatColumnKind;
  /** 计算口径：分子分母各是什么。维度列写它标识的是谁。 */
  definition: string;
  aggregate: StatAggregate;
  format: StatFormat;
};

export type StatSourceSpec = {
  /** 源过程表 / 资产表的表名。**必须是权威表**，不得是另一张 stat_ 表。 */
  table: string;
  condition: string;
};

export type StatTableSpec = {
  code: string;
  name: string;
  level: StatLevel;
  physicalTable: string;
  periodUnit: 'month';
  periodWindow: string;
  /** 详情页第 1 段：这张表回答什么问题。 */
  question: string;
  /** 卡片上「最近一期」摆出来的几个数，取 `columns` 里 kind 非 dimension 的 key。 */
  headlineKeys: readonly string[];
  /** 12 期小柱图用哪一列。 */
  trendKey: string;
  columns: readonly StatColumn[];
  sources: readonly StatSourceSpec[];
};

const MONTH_WINDOW = '滚动 12 个月';

/**
 * 六张统计表的口径（C-168 A2）。
 *
 * **顺序即界面顺序**：卡片墙按本数组的次序摆，同一层级的两张挨着。
 */
export const STAT_TABLES: readonly StatTableSpec[] = [
  {
    code: 'STAT-ENT-001',
    name: '工时构成月报',
    level: 'ent',
    physicalTable: 'stat_ent_workhour_monthly',
    periodUnit: 'month',
    periodWindow: MONTH_WINDOW,
    question:
      '这个月企业的总工时被怎么分掉了：多少是编制里本来就有的，多少已经被 ATC 覆盖到，' +
      '覆盖到的那部分里多少由中枢完成、多少仍由人完成。',
    headlineKeys: [
      'establishment_work_minutes',
      'mapped_work_minutes',
      'ai_completed_work_minutes',
    ],
    trendKey: 'ai_completed_work_minutes',
    columns: [
      {
        key: 'establishment_work_minutes',
        label: '编制总工时（分钟）',
        kind: 'metric',
        definition:
          '分子：Σ（当期有效编制人数 × 8 小时 × 60 分钟 × 当期工作日数）。无分母，它本身是后面三个数的分母。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'mapped_work_minutes',
        label: '已 mapping 工时（分钟）',
        kind: 'metric',
        definition:
          '分子：Σ（已立卡的基线日总耗时 × 当期工作日数）。分母：编制总工时。' +
          '两者的比即 mapping 覆盖率——建设进度，与 AI 替代率不是一个数。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'ai_completed_work_minutes',
        label: 'AI 完成工时（分钟）',
        kind: 'metric',
        definition:
          '分子：Σ（taskrun 中 actor_type = hub 且已结束的执行时长）。分母：已 mapping 工时。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'human_completed_work_minutes',
        label: '人工完成工时（分钟）',
        kind: 'metric',
        definition:
          '分子：Σ（taskrun 中 actor_type ∈ {human, hybrid} 且已结束的执行时长）。分母：已 mapping 工时。',
        aggregate: 'sum',
        format: 'integer',
      },
    ],
    sources: [
      {
        table: 'person_assignment',
        condition:
          '在岗区间与当期重叠的行；编制人数取 position_headcount 当期有效值。' +
          '归业务日走 app.biz_date(tenant_id, ·)，锚点是企业设定的时区。',
      },
      {
        table: 'taskrun',
        condition:
          'is_latest 且 ended_at 非空；按 app.biz_date(tenant_id, started_at) 归业务日，' +
          '再按业务日所属月归期。',
      },
    ],
  },
  {
    code: 'STAT-ENT-002',
    name: 'ATC 资产月报',
    level: 'ent',
    physicalTable: 'stat_ent_atcasset_monthly',
    periodUnit: 'month',
    periodWindow: MONTH_WINDOW,
    question:
      '这个月资产盘子长了多少、卡都停在什么状态、运营卡与建设卡各占多少。' +
      '数的是卡，不是执行——资产规模与通量不可互相替代。',
    headlineKeys: ['closing_card_count', 'added_in_month_card_count', 'closing_active_card_count'],
    trendKey: 'closing_card_count',
    columns: [
      {
        key: 'closing_card_count',
        label: '期末在册卡数（张）',
        kind: 'metric',
        definition: '分子：期末仍在册的 ATC 张数（含已归档）。无分母。下面两组分布之和都等于它。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'added_in_month_card_count',
        label: '本月新增卡数（张）',
        kind: 'metric',
        definition: '分子：atc_status_history 中 seq = 1 且落在当期内的卡数。无分母。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'retired_in_month_card_count',
        label: '本月归档卡数（张）',
        kind: 'metric',
        definition: '分子：当期内 to_status 变为 retired 的卡数。归档不释放编码。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'closing_proposed_card_count',
        label: '期末待建卡数（张）',
        kind: 'metric',
        definition: '分子：期末状态为 proposed 的卡数。待建不是资产，不进达成率与替代率的分母。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'closing_draft_card_count',
        label: '期末草稿卡数（张）',
        kind: 'metric',
        definition: '分子：期末状态为 draft 的卡数。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'closing_in_review_card_count',
        label: '期末评审中卡数（张）',
        kind: 'metric',
        definition: '分子：期末状态为 in_review 的卡数。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'closing_certified_card_count',
        label: '期末定义认证卡数（张）',
        kind: 'metric',
        definition:
          '分子：期末状态为 certified 的卡数。定义认证准入：四段齐 + TEST 至少一条带 key 的可判定断言，不绑 determinism。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'closing_active_card_count',
        label: '期末在役卡数（张）',
        kind: 'metric',
        definition: '分子：期末状态为 active 的卡数。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'closing_suspended_card_count',
        label: '期末暂停卡数（张）',
        kind: 'metric',
        definition: '分子：期末状态为 suspended 的卡数。暂停不是归档。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'closing_retired_card_count',
        label: '期末已归档卡数（张）',
        kind: 'metric',
        definition: '分子：期末状态为 retired 的卡数。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'closing_operational_card_count',
        label: '期末运营卡数（张）',
        kind: 'metric',
        definition: '分子：期末 cls = operational 的卡数。与下一列之和等于期末在册卡数。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'closing_construction_card_count',
        label: '期末建设卡数（张）',
        kind: 'metric',
        definition:
          '分子：期末 cls = construction 的卡数。' +
          '运营 / 建设与客户 / 我方是两个轴，不得互相推导。',
        aggregate: 'sum',
        format: 'integer',
      },
    ],
    sources: [
      {
        table: 'atc_status_history',
        condition:
          '仅追加，故任一历史时点的期末状态都可重算：取每张卡 changed_at ≤ 期末的最后一条 to_status。',
      },
      {
        table: 'atc',
        condition: '取 cls 做类别分布。cls 非空无默认，故不会有落不进任一类的卡。',
      },
    ],
  },
  {
    code: 'STAT-DEP-001',
    name: '部门卡位填充月报',
    level: 'dep',
    physicalTable: 'stat_dep_cardslot_monthly',
    periodUnit: 'month',
    periodWindow: MONTH_WINDOW,
    question:
      '这个部门规划了多少个卡位、已经立起来多少张卡、还空着多少。' +
      '空位是待建存量，不是缺陷——它画出来的是「这栋楼还有多少间房要开」。',
    headlineKeys: ['planned_card_slot_count', 'filled_card_slot_count', 'vacant_card_slot_count'],
    trendKey: 'filled_card_slot_count',
    columns: [
      {
        key: 'department_code',
        label: '部门编码',
        kind: 'dimension',
        definition: '这一行代表哪个部门。编码与名称随行快照——这张表要能单独打印存档。',
        aggregate: 'none',
        format: 'text',
      },
      {
        key: 'department_name',
        label: '部门名称',
        kind: 'dimension',
        definition: '同上。',
        aggregate: 'none',
        format: 'text',
      },
      {
        key: 'planned_card_slot_count',
        label: '卡位总数（个）',
        kind: 'metric',
        definition:
          '分子：Σ（该部门下各岗位的 planned_card_count）。无分母。' +
          '人填的调研结论，不得由人数推算。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'filled_card_slot_count',
        label: '已立卡数（张）',
        kind: 'metric',
        definition: '分子：该部门下 status ≠ retired 的 ATC 张数。分母：卡位总数。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'vacant_card_slot_count',
        label: '空位数（个）',
        kind: 'metric',
        definition: '卡位总数 − 已立卡数。三者相加必须成立，否则界面画出来的分格会少一块而不报错。',
        aggregate: 'sum',
        format: 'integer',
      },
    ],
    sources: [
      {
        table: 'position',
        condition:
          '按部门归集 planned_card_count。为 null 的岗位记为「还没调研」，不记 0——' +
          '0 是「调研过，结论是这个岗位不需要卡」。',
      },
      { table: 'atc', condition: 'status ≠ retired 的卡按 position_id 归到部门。' },
    ],
  },
  {
    code: 'STAT-DEP-002',
    name: '部门执行量月报',
    level: 'dep',
    physicalTable: 'stat_dep_execution_monthly',
    periodUnit: 'month',
    periodWindow: MONTH_WINDOW,
    question: '这个部门这个月跑了多少次 TaskRun，其中成功多少、失败多少，成功率是多少。',
    headlineKeys: ['taskrun_count', 'success_taskrun_count', 'failed_taskrun_count'],
    trendKey: 'taskrun_count',
    columns: [
      {
        key: 'department_code',
        label: '部门编码',
        kind: 'dimension',
        definition: '这一行代表哪个部门。',
        aggregate: 'none',
        format: 'text',
      },
      {
        key: 'department_name',
        label: '部门名称',
        kind: 'dimension',
        definition: '同上。',
        aggregate: 'none',
        format: 'text',
      },
      {
        key: 'taskrun_count',
        label: 'TaskRun 次数（次）',
        kind: 'metric',
        definition:
          '分子：该部门下各卡当期 is_latest 的执行记录条数。无分母。' +
          'TaskRun 不入资产树，它是通量不是资产。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'success_taskrun_count',
        label: '成功次数（次）',
        kind: 'metric',
        definition: '分子：当期 status = success 的执行记录条数。分母：TaskRun 次数。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'failed_taskrun_count',
        label: '失败次数（次）',
        kind: 'metric',
        definition: '分子：当期 status = failed 的执行记录条数。分母：TaskRun 次数。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'success_rate',
        label: '成功率',
        kind: 'derived',
        definition:
          '成功次数 ÷ TaskRun 次数，由库派生（generated stored），不另存一个数。' +
          '总次数为 0 时是「未发生」不是 0%——「一次都没跑」与「跑了但全失败」必须分得开。',
        aggregate: 'none',
        format: 'percent',
      },
    ],
    sources: [
      {
        table: 'taskrun',
        condition:
          'is_latest 的执行记录，按 app.biz_date(tenant_id, started_at) 归业务日再归期；' +
          '经 atc → position → department 归到部门。',
      },
    ],
  },
  {
    code: 'STAT-POS-001',
    name: '岗位执行量月报',
    level: 'pos',
    physicalTable: 'stat_pos_execution_monthly',
    periodUnit: 'month',
    periodWindow: MONTH_WINDOW,
    question:
      '这个岗位这个月是哪几张卡在跑、各跑了多少次，以及每张卡里中枢、人、人机协同各占多少。',
    headlineKeys: ['taskrun_count', 'hub_taskrun_count', 'human_taskrun_count'],
    trendKey: 'taskrun_count',
    columns: [
      {
        key: 'atc_code',
        label: 'ATC 编码',
        kind: 'dimension',
        definition: '这一行代表哪一张卡。逐卡一行——汇总到岗位就答不了「是哪张卡在跑」。',
        aggregate: 'none',
        format: 'text',
      },
      {
        key: 'atc_name',
        label: 'ATC 名称',
        kind: 'dimension',
        definition: '同上。',
        aggregate: 'none',
        format: 'text',
      },
      {
        key: 'taskrun_count',
        label: 'TaskRun 次数（次）',
        kind: 'metric',
        definition: '分子：该卡当期 is_latest 的执行记录条数。无分母。下面三列之和等于它。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'hub_taskrun_count',
        label: '中枢完成次数（次）',
        kind: 'metric',
        definition: '分子：当期 actor_type = hub 的执行记录条数。分母：TaskRun 次数。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'human_taskrun_count',
        label: '人工完成次数（次）',
        kind: 'metric',
        definition: '分子：当期 actor_type = human 的执行记录条数。分母：TaskRun 次数。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'hybrid_taskrun_count',
        label: '人机协同次数（次）',
        kind: 'metric',
        definition: '分子：当期 actor_type = hybrid 的执行记录条数。分母：TaskRun 次数。',
        aggregate: 'sum',
        format: 'integer',
      },
    ],
    sources: [
      {
        table: 'taskrun',
        condition:
          'is_latest 的执行记录，按 app.biz_date(tenant_id, started_at) 归业务日再归期；逐 atc_id 分组。',
      },
    ],
  },
  {
    code: 'STAT-POS-002',
    name: '岗位基线压缩月报',
    level: 'pos',
    physicalTable: 'stat_pos_baseline_monthly',
    periodUnit: 'month',
    periodWindow: MONTH_WINDOW,
    question:
      '这个岗位每张卡改造前人做一次要多久、改造后实际跑一次多久，两者的差就是这张卡的改造成效。',
    headlineKeys: ['baseline_duration_min', 'actual_avg_duration_min', 'taskrun_count'],
    trendKey: 'actual_avg_duration_min',
    columns: [
      {
        key: 'atc_code',
        label: 'ATC 编码',
        kind: 'dimension',
        definition: '这一行代表哪一张卡。',
        aggregate: 'none',
        format: 'text',
      },
      {
        key: 'atc_name',
        label: 'ATC 名称',
        kind: 'dimension',
        definition: '同上。',
        aggregate: 'none',
        format: 'text',
      },
      {
        key: 'baseline_duration_min',
        label: '基线单次耗时（分钟）',
        kind: 'metric',
        definition:
          '取 atc.baseline_duration_min：改造前人做一次要多久，建卡那一刻的快照，随卡终身。' +
          '为 null 的卡不进本表——没填就是没填，不补一个数。',
        aggregate: 'avg',
        format: 'integer',
      },
      {
        key: 'actual_avg_duration_min',
        label: '实际单次均耗（分钟）',
        kind: 'metric',
        definition:
          '分子：当期该卡的执行总时长。分母：当期执行次数。' +
          '当期零执行时是「未发生」不是 0——0 分钟意味着零耗时完成，那是这张表里最容易被读成成功的假值。',
        aggregate: 'avg',
        format: 'decimal1',
      },
      {
        key: 'taskrun_count',
        label: 'TaskRun 次数（次）',
        kind: 'metric',
        definition: '分子：当期该卡 is_latest 的执行记录条数。它是上一列的分母。',
        aggregate: 'sum',
        format: 'integer',
      },
      {
        key: 'compressed_minutes_per_run',
        label: '单次压缩分钟数（分钟）',
        kind: 'derived',
        definition:
          '基线单次耗时 − 实际单次均耗，由库派生（generated stored）。为负表示比改造前还慢。',
        aggregate: 'none',
        format: 'decimal1',
      },
      {
        key: 'compression_ratio',
        label: '压缩率',
        kind: 'derived',
        definition:
          '（基线单次耗时 − 实际单次均耗）÷ 基线单次耗时，由库派生。' +
          '基线与实际均耗永不合一：只留一个压缩率，改造前的分母就没人看得见了。',
        aggregate: 'none',
        format: 'percent',
      },
    ],
    sources: [
      {
        table: 'atc',
        condition: '取 baseline_duration_min 作为基线单次耗时。为 null 的卡不进本表。',
      },
      {
        table: 'taskrun',
        condition: 'is_latest 且 ended_at 非空的执行记录，按业务日归期，逐 atc_id 求均耗。',
      },
    ],
  },
];

export function statTableOf(code: string): StatTableSpec | undefined {
  return STAT_TABLES.find((spec) => spec.code === code);
}

export function statTablesOfLevel(level: StatLevel): StatTableSpec[] {
  return STAT_TABLES.filter((spec) => spec.level === level);
}

/* ═══════════════════════════════════════════════════════════════════
   接口载荷
   ═══════════════════════════════════════════════════════════════════ */

/**
 * 一格数。
 *
 * `null` 是**「这一期没有这个数」**，不是 0——与首屏五数同一条裁定（C-099R 三、P-13）。
 * 维度列是字符串，指标列是数字。
 */
export const statCellSchema = z.union([z.number(), z.string(), z.null()]);
export type StatCell = z.infer<typeof statCellSchema>;

export const statRowSchema = z.object({
  /** 期次：该期的月首日，`YYYY-MM-DD`。 */
  periodMonth: z.string(),
  /** 按物理列名取值。**只是传输形状**——库里是逐列存的，不是 jsonb（C-168 A1）。 */
  values: z.record(z.string(), statCellSchema),
});
export type StatRow = z.infer<typeof statRowSchema>;

/** 校验状态。**两处同时为 null 才是「未核」**，与库里的 CHECK 一致。 */
export const statVerifyResultSchema = z.enum(['consistent', 'inconsistent']);

export const statVerifyStatusSchema = z.object({
  lastVerifiedAt: z.string().nullable(),
  lastResult: statVerifyResultSchema.nullable(),
});
export type StatVerifyStatus = z.infer<typeof statVerifyStatusSchema>;

export const statCardSchema = z.object({
  code: z.string(),
  name: z.string(),
  level: statLevelSchema,
  physicalTable: z.string(),
  periodWindow: z.string(),
  /** 最近一期的月首日。**null = 这张表一行都没有**（客户实例即如此）。 */
  latestPeriod: z.string().nullable(),
  /** 库里一共有几期。 */
  periodCount: z.number(),
  /** 卡片上摆出来的几个数，已按 `aggregate` 合过。 */
  headline: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      value: z.number().nullable(),
      aggregate: z.enum(['sum', 'avg', 'none']),
    }),
  ),
  /** 12 期小柱图。`value` 为 null 的期照样占一格——**空一格与零高一格是两回事**。 */
  trend: z.array(z.object({ periodMonth: z.string(), value: z.number().nullable() })),
  trendLabel: z.string(),
  verify: statVerifyStatusSchema,
  /** 本表各行的数据来源标注：`simulated` 表示本轮的模拟数据，`recomputed` 表示已重算。 */
  dataOrigin: z.enum(['recomputed', 'simulated', 'mixed']).nullable(),
  /** 建设卡与运营卡的编码。**null = 登记不完整**，界面照实说，不隐藏。 */
  constructionAtcCode: z.string().nullable(),
  operationalAtcCode: z.string().nullable(),
});
export type StatCard = z.infer<typeof statCardSchema>;

export const statCardListSchema = z.object({
  level: statLevelSchema,
  /** 站在哪个节点上看。企业站为 null。 */
  scopeName: z.string().nullable(),
  rows: z.array(statCardSchema),
});
export type StatCardList = z.infer<typeof statCardListSchema>;

export const statVerificationSchema = z.object({
  periodMonth: z.string(),
  verifiedAt: z.string(),
  isConsistent: z.boolean(),
  differenceNote: z.string().nullable(),
  verifiedBy: z.string().nullable(),
});
export type StatVerification = z.infer<typeof statVerificationSchema>;

export const statChangeSchema = z.object({
  seq: z.number(),
  changedAt: z.string(),
  changeKind: z.string(),
  changeNote: z.string(),
  changedBy: z.string().nullable(),
});
export type StatChange = z.infer<typeof statChangeSchema>;

export const statDetailSchema = z.object({
  card: statCardSchema,
  /** 数值表的行，最新一期在前。 */
  rows: z.array(statRowSchema),
  /** 每一行的来源标注，与 `rows` 等长同序。 */
  origins: z.array(z.enum(['recomputed', 'simulated'])),
  /** 第 6 段：重算校验记录。**空数组即「尚未校验」**，界面不显示「一致」。 */
  verifications: z.array(statVerificationSchema),
  /** 第 7 段：变更记录。 */
  changes: z.array(statChangeSchema),
  /** 第 5 段：数据来源。库里登记的那一份，与本文件的 `sources` 应当一致。 */
  registeredSources: z.array(z.object({ table: z.string(), condition: z.string() })),
});
export type StatDetail = z.infer<typeof statDetailSchema>;
